/**
 * Message syntax: a small, dependency-free subset of ICU MessageFormat.
 *
 *   Plain text                      'Save'
 *   Simple argument                 'Showing {shown} of {total}'
 *   Plural (Intl.PluralRules)       '{count, plural, =0 {No users} one {# user} other {# users}}'
 *   Select                          '{kind, select, shared {Shared mailbox} other {Mailbox}}'
 *   Ordinal                         '{n, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}'
 *   Number                          '{size, number}'
 *   Quoting (ICU apostrophe rules)  "'{'literal braces'}'" and "''" for one apostrophe
 *
 * `#` inside a plural branch is the locale-formatted number. A missing argument stays visible
 * as `{name}` so a gap never breaks the UI. The same parser validates language packs
 * (placeholders and syntax), so what passes validation is exactly what the runtime can render.
 */

export type MessageParams = Readonly<Record<string, string | number>>;

type Node =
  | { kind: 'text'; value: string }
  | { kind: 'pound' }
  | { kind: 'arg'; name: string }
  | { kind: 'number'; name: string }
  | { kind: 'plural'; name: string; ordinal: boolean; branches: ReadonlyMap<string, Node[]> }
  | { kind: 'select'; name: string; branches: ReadonlyMap<string, Node[]> };

/** Thrown for malformed messages; `message` says what and where. */
export class MessageSyntaxError extends Error {
  constructor(message: string, readonly offset: number) {
    super(`${message} (at character ${offset})`);
    this.name = 'MessageSyntaxError';
  }
}

const NAME = /^[A-Za-z_][\w]*$/;
const SELECTOR = /^(=\d+|[A-Za-z_][\w-]*)$/;

class Parser {
  private pos = 0;

  constructor(private readonly src: string) {}

  parse(): Node[] {
    const nodes = this.message(false, 0);
    if (this.pos < this.src.length) throw new MessageSyntaxError("Unmatched '}'", this.pos);
    return nodes;
  }

  /** Parses until an unquoted `}` at this level (not consumed) or the end. */
  private message(inPlural: boolean, depth: number): Node[] {
    if (depth > 4) throw new MessageSyntaxError('Message nested too deeply', this.pos);
    const nodes: Node[] = [];
    let text = '';
    const flush = () => {
      if (text) nodes.push({ kind: 'text', value: text });
      text = '';
    };
    while (this.pos < this.src.length) {
      const ch = this.src[this.pos]!;
      if (ch === "'") {
        text += this.quoted(inPlural);
        continue;
      }
      if (ch === '}') break;
      if (ch === '{') {
        flush();
        nodes.push(this.argument(depth));
        continue;
      }
      if (ch === '#' && inPlural) {
        flush();
        nodes.push({ kind: 'pound' });
        this.pos++;
        continue;
      }
      text += ch;
      this.pos++;
    }
    flush();
    return nodes;
  }

  /** ICU apostrophe rules: `''` is one apostrophe; `'` before a syntax char starts a quoted run. */
  private quoted(inPlural: boolean): string {
    const next = this.src[this.pos + 1];
    if (next === "'") {
      this.pos += 2;
      return "'";
    }
    if (next === '{' || next === '}' || (inPlural && next === '#')) {
      const end = this.src.indexOf("'", this.pos + 1);
      if (end < 0) throw new MessageSyntaxError('Unterminated quote', this.pos);
      const literal = this.src.slice(this.pos + 1, end);
      this.pos = end + 1;
      return literal;
    }
    this.pos++;
    return "'";
  }

  private argument(depth: number): Node {
    const start = this.pos;
    this.pos++; // {
    const name = this.token(',}');
    if (!NAME.test(name)) throw new MessageSyntaxError(`Invalid argument name "${name}"`, start);
    if (this.peek() === '}') {
      this.pos++;
      return { kind: 'arg', name };
    }
    this.expect(',');
    const type = this.token(',}');
    if (type === 'number') {
      if (this.peek() === ',') {
        this.pos++;
        this.token('}'); // style ignored: numbers use the locale default
      }
      this.expect('}');
      return { kind: 'number', name };
    }
    if (type !== 'plural' && type !== 'selectordinal' && type !== 'select') {
      throw new MessageSyntaxError(`Unsupported argument type "${type}"`, start);
    }
    this.expect(',');
    const isPlural = type !== 'select';
    const branches = new Map<string, Node[]>();
    for (;;) {
      this.skipSpace();
      if (this.peek() === '}') break;
      const at = this.pos;
      const selector = this.token('{');
      if (!SELECTOR.test(selector)) throw new MessageSyntaxError(`Invalid selector "${selector}"`, at);
      if (branches.has(selector)) throw new MessageSyntaxError(`Duplicate selector "${selector}"`, at);
      this.expect('{');
      branches.set(selector, this.message(isPlural, depth + 1));
      this.expect('}');
    }
    this.expect('}');
    if (!branches.has('other')) throw new MessageSyntaxError(`"${name}" (${type}) needs an "other" branch`, start);
    return isPlural ? { kind: 'plural', name, ordinal: type === 'selectordinal', branches } : { kind: 'select', name, branches };
  }

  private token(stops: string): string {
    this.skipSpace();
    const start = this.pos;
    while (this.pos < this.src.length && !stops.includes(this.src[this.pos]!)) this.pos++;
    if (this.pos >= this.src.length) throw new MessageSyntaxError("Unclosed '{'", start);
    return this.src.slice(start, this.pos).trim();
  }

  private expect(ch: string): void {
    this.skipSpace();
    if (this.src[this.pos] !== ch) throw new MessageSyntaxError(`Expected '${ch}'`, this.pos);
    this.pos++;
  }

  private peek(): string | undefined {
    this.skipSpace();
    return this.src[this.pos];
  }

  private skipSpace(): void {
    while (this.pos < this.src.length && /\s/.test(this.src[this.pos]!)) this.pos++;
  }
}

/** Parses a message; throws `MessageSyntaxError` when it is malformed. */
function parse(message: string): Node[] {
  return new Parser(message).parse();
}

/** Argument names used anywhere in a message (also inside plural/select branches). Throws on bad syntax. */
export function messageArguments(message: string): Set<string> {
  const names = new Set<string>();
  const walk = (nodes: readonly Node[]) => {
    for (const node of nodes) {
      if (node.kind === 'text' || node.kind === 'pound') continue;
      names.add(node.name);
      if (node.kind === 'plural' || node.kind === 'select') node.branches.forEach(walk);
    }
  };
  walk(parse(message));
  return names;
}

function render(nodes: readonly Node[], params: MessageParams | undefined, locale: string, pound: string | undefined): string {
  let out = '';
  for (const node of nodes) {
    switch (node.kind) {
      case 'text':
        out += node.value;
        break;
      case 'pound':
        out += pound ?? '#';
        break;
      case 'arg': {
        const value = params?.[node.name];
        out += value === undefined ? `{${node.name}}` : String(value);
        break;
      }
      case 'number': {
        const value = params?.[node.name];
        out += typeof value === 'number' ? new Intl.NumberFormat(locale).format(value) : value === undefined ? `{${node.name}}` : value;
        break;
      }
      case 'plural': {
        const value = Number(params?.[node.name]);
        if (params?.[node.name] === undefined || Number.isNaN(value)) {
          out += `{${node.name}}`;
          break;
        }
        const exact = node.branches.get(`=${value}`);
        const category = new Intl.PluralRules(locale, { type: node.ordinal ? 'ordinal' : 'cardinal' }).select(value);
        const branch = exact ?? node.branches.get(category) ?? node.branches.get('other') ?? [];
        out += render(branch, params, locale, new Intl.NumberFormat(locale).format(value));
        break;
      }
      case 'select': {
        const value = params?.[node.name];
        const branch = (value === undefined ? undefined : node.branches.get(String(value))) ?? node.branches.get('other') ?? [];
        out += render(branch, params, locale, pound);
        break;
      }
    }
  }
  return out;
}

/** A compiled message: render it with parameters. */
export type CompiledMessage = (params?: MessageParams) => string;

/**
 * Compiles a message for a locale. Malformed messages (should never pass pack validation)
 * render as their raw text instead of throwing.
 */
export function compileMessage(message: string, locale: string): CompiledMessage {
  let nodes: Node[];
  try {
    nodes = parse(message);
  } catch {
    return () => message;
  }
  const first = nodes[0];
  if (nodes.length === 1 && first?.kind === 'text') {
    const text = first.value;
    return () => text;
  }
  return (params) => render(nodes, params, locale, undefined);
}
